import { formatDateTime } from "@/lib/format-number";
import { getLocaleMeta } from "@/lib/i18n/locales";

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A `<input type="date">` value ("2026-10-05") written the way the reader's language
 * writes a date — "October 5, 2026", "5 octobre 2026" — or "" when it is empty or not a
 * real calendar date.
 *
 * Built from the parts in local time on purpose: `new Date("2026-10-05")` is midnight
 * UTC, which is the evening of October 4 anywhere west of Greenwich.
 */
export function isoDateToLocal(iso: string, locale: string): string {
  const match = ISO_DATE.exec(iso.trim());
  if (!match) {
    return "";
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return "";
  }
  return formatDateTime(locale, date, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * "A, B and C" in the reader's language, through `Intl.ListFormat`; a plain
 * comma-joined list where the runtime has none.
 */
export function listFormat(items: readonly string[], locale: string): string {
  const tag = getLocaleMeta(locale)?.hreflang ?? "en";
  try {
    return new Intl.ListFormat(tag, {
      style: "long",
      type: "conjunction",
    }).format(items);
  } catch {
    return items.join(", ");
  }
}
